import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  AddCircleOutlineIcon,
  Button,
  Checkbox,
  EditableColumn,
  EditIcon,
  Radio,
  showToast,
  Skeleton,
  Switch,
  Tag,
  Text,
  TrashIcon,
} from '@mercai/clever'

import { DECIMAL_PLACES_DEFAULT, MERCAI_WAREHOUSES_OWNER_EMAIL } from '@/constants'
import { formatCurrency, onConfirmWarningMessage } from '@/helpers'
import { ROUTES_ADMIN } from '@/router'

import { QUERIES_ADMIN, useSaleOrderItemUpdate } from '@/services'

import {
  GeneratePurchaseOrderPayloadSupplyItemProps,
  ProductQualityCategoryEnum,
  SupplyItemsPreviewResponseSaleOrderItem,
  SupplyItemsPreviewResponseSupplyOrderItem,
} from '@/types'

import { useQueryClient } from '@tanstack/react-query'
import { SectionHeader } from '../../../../components/section-header'

import {
  formattedExcludedItemsToShow,
  getAvailableQuantity,
  getGeneratePoQuantity,
  getSupplyItemCompletionStatus,
  getSupplyItemsByPayload,
  getSupplyOrderItemValueByCurrency,
  totalSupplyOrderItemValue,
} from './select-supplier-order-items.constants'

import {
  ExcludedItemsProps,
  SelectSupplierOrderItemsProps,
} from './select-supplier-order-items.type'

import styles from './select-supplier-order-items.module.css'

export const SelectSupplierOrderItems = ({
  disabled,
  position,
  values,
  setValues,
  onShowAddSaleOrders,
  isLoading,
  saleOrdersToPayload,
  setSaleOrdersToPayload,
  preview,
  onChangeCurrency,
}: SelectSupplierOrderItemsProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const queryClient = useQueryClient()

  const { mutateAsync: SaleOrderItemMutateAsync } = useSaleOrderItemUpdate()

  const handleSaleOrderItemUpdate = async (
    newPrice: string,
    saleOrderItemUuid: string,
    dollarExchangeRate: number = 1,
  ) => {
    const formattedPrice =
      values?.currency === 'USD'
        ? Number(newPrice)
        : Number((Number(newPrice) / dollarExchangeRate).toFixed(DECIMAL_PLACES_DEFAULT))

    const confirm = await onConfirmWarningMessage(
      t('componentSelectSaleOrdersUpdateSaleOrderItemConfirmLabel'),
      t('componentSelectSaleOrdersUpdateSaleOrderItemConfirmMessage'),
      t('componentSelectSaleOrdersUpdateSaleOrderItemConfirmConfirmLabel'),
      t('componentSelectSaleOrdersUpdateSaleOrderItemConfirmCancelLabel'),
    )

    if (!confirm) {
      return
    }

    try {
      const result = await SaleOrderItemMutateAsync({
        uuid: saleOrderItemUuid,
        cost: formattedPrice,
      })

      if (!result?.success) {
        showToast({
          variant: 'error',
          title: t('componentSelectSaleOrdersUpdateSaleOrderItemErrorMessage'),
        })
        return
      }

      showToast({
        variant: 'success',
        title: t('componentSelectSaleOrdersUpdateSaleOrderItemSuccessMessage'),
      })

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.SUPPLIER_ITEMS_PREVIEW.module],
      })

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.GENERATE_PURCHASE_ORDER_PREVIEW.module],
      })
    } catch (error) {
      console.error('Error updating sale order item:', error)
    }
  }

  const [excludeItems, setExcludeItems] = useState<ExcludedItemsProps>({})

  const formattedExcludedItems = useMemo(
    () => formattedExcludedItemsToShow(excludeItems),
    [excludeItems],
  )

  const toggleExcludeItem = (
    item: SupplyItemsPreviewResponseSupplyOrderItem,
    saleOrderUuid: string,
  ) => {
    setExcludeItems((prev) => {
      const newExcludeItems = { ...prev }
      if (newExcludeItems[item?.uuid || '']) {
        delete newExcludeItems[item?.uuid || '']
      } else {
        setSaleOrdersToPayload((prevPayload) => {
          const newPayload = { ...prevPayload }
          if (newPayload?.[saleOrderUuid]?.[item?.uuid || '']) {
            delete newPayload[saleOrderUuid][item?.uuid || '']
          }

          return newPayload
        })

        newExcludeItems[item?.uuid || ''] = {
          ...item,
          saleOrderUuid,
          saleOrderReference:
            preview?.saleOrders?.find((so) => so.uuid === saleOrderUuid)
              ?.reference || '',
        }
      }
      return newExcludeItems
    })
  }

  const removeSaleOrder = (uuid: string) => {
    setSaleOrdersToPayload((prevPayload) => {
      const newPayload = { ...prevPayload }
      delete newPayload[uuid]

      return newPayload
    })

    setValues((prev) => ({
      ...prev,
      saleOrders: prev?.saleOrders?.filter((so) => so.uuid !== uuid),
    }))
  }

  const toggleSaleOrdersToPayload = (
    saleOrderUuid: string,
    saleOrderItem: SupplyItemsPreviewResponseSaleOrderItem,
    supplyItemUuid: string | undefined,
    item?: SupplyItemsPreviewResponseSupplyOrderItem,
    missingQuantity: number = 0,
  ) => {
    const saleOrderItemUuid = saleOrderItem?.uuid || ''

    const valueToGeneratePO = saleOrdersToPayload?.[saleOrderUuid]?.[
      saleOrderItemUuid
    ]?.find((supplyItem) => supplyItem?.generatePo)

    if (!supplyItemUuid) {
      setSaleOrdersToPayload((prevPayload) => {
        let newPayload = { ...prevPayload }
        if (!newPayload?.[saleOrderUuid]) {
          newPayload[saleOrderUuid] = {}
        }

        if (!newPayload?.[saleOrderUuid]?.[saleOrderItemUuid]) {
          newPayload[saleOrderUuid][saleOrderItemUuid] = []
        }

        if (valueToGeneratePO) {
          newPayload = {
            ...newPayload,
            [saleOrderUuid]: {
              ...newPayload[saleOrderUuid],
              [saleOrderItemUuid]: newPayload[saleOrderUuid][
                saleOrderItemUuid
              ].filter((supplyItem) => !supplyItem?.generatePo),
            },
          }
        } else {
          const quantity = getGeneratePoQuantity(
            saleOrderUuid,
            saleOrderItem,
            newPayload,
          )

          newPayload[saleOrderUuid][saleOrderItemUuid].push({
            generatePo: true,
            quantity,
          })
        }
        return newPayload
      })
      return
    }

    if (!item) {
      return
    }

    setSaleOrdersToPayload((prevPayload) => {
      const newPayload = { ...prevPayload }
      if (
        !newPayload?.[saleOrderUuid]?.[saleOrderItemUuid]?.find(
          (supplyItem) => supplyItem?.uuid === supplyItemUuid,
        )
      ) {
        if (!newPayload[saleOrderUuid]) {
          newPayload[saleOrderUuid] = {}
        }

        if (!newPayload[saleOrderUuid][saleOrderItemUuid]) {
          newPayload[saleOrderUuid][saleOrderItemUuid] = []
        }

        const totalInventoryQuantity = newPayload[saleOrderUuid][
          saleOrderItemUuid
        ]
          .filter((supplyItem) => supplyItem?.generatePo === false)
          .reduce((acc, supplyItem) => acc + (supplyItem?.quantity || 0), 0)

        const quantity = valueToGeneratePO?.quantity
          ? missingQuantity - totalInventoryQuantity >
            (item?.availableQuantity || 0)
            ? item.availableQuantity || 0
            : missingQuantity - totalInventoryQuantity
          : 0

        newPayload[saleOrderUuid][saleOrderItemUuid] = [
          ...newPayload[saleOrderUuid][saleOrderItemUuid],
          {
            generatePo: false,
            quantity,
            uuid: supplyItemUuid,
          },
        ]

        if (valueToGeneratePO) {
          if (totalInventoryQuantity + quantity === missingQuantity) {
            newPayload[saleOrderUuid][saleOrderItemUuid] = [
              ...newPayload[saleOrderUuid][saleOrderItemUuid].filter(
                (supplyItem) => !supplyItem?.generatePo,
              ),
            ]

            return newPayload
          } else {
            newPayload[saleOrderUuid][saleOrderItemUuid] = [
              ...newPayload[saleOrderUuid][saleOrderItemUuid].filter(
                (supplyItem) => !supplyItem?.generatePo,
              ),
              {
                generatePo: true,
                quantity: missingQuantity - (totalInventoryQuantity + quantity),
              },
            ]
          }
        }
      } else {
        newPayload[saleOrderUuid][saleOrderItemUuid] = newPayload[
          saleOrderUuid
        ][saleOrderItemUuid].filter(
          (supplyItem) => supplyItem?.uuid !== supplyItemUuid,
        )
      }

      if (valueToGeneratePO) {
        const quantity = getGeneratePoQuantity(
          saleOrderUuid,
          saleOrderItem,
          newPayload,
        )

        newPayload[saleOrderUuid][saleOrderItemUuid] = [
          ...newPayload[saleOrderUuid][saleOrderItemUuid].filter(
            (supplyItem) => !supplyItem?.generatePo,
          ),
          {
            generatePo: true,
            quantity,
          },
        ]
      }

      return newPayload
    })
  }

  const handleChangeQuantity = (
    saleOrderUuid: string,
    saleOrderItem: SupplyItemsPreviewResponseSaleOrderItem,
    supplyItemUuid: string,
    quantity: number,
  ) => {
    const saleOrderItemUuid = saleOrderItem?.uuid || ''
    setSaleOrdersToPayload((prevPayload) => {
      let newPayload = { ...prevPayload }
      const items = newPayload?.[saleOrderUuid]?.[saleOrderItemUuid]

      if (items) {
        if (
          !newPayload[saleOrderUuid][saleOrderItemUuid].find(
            (supplyItem) => supplyItem?.uuid === supplyItemUuid,
          )
        ) {
          newPayload[saleOrderUuid][saleOrderItemUuid].push({
            generatePo: false,
            quantity,
            uuid: supplyItemUuid,
          })
        } else {
          newPayload[saleOrderUuid][saleOrderItemUuid] = items.map((item) =>
            item.uuid === supplyItemUuid ? { ...item, quantity } : item,
          )
        }
      }

      const valueToGeneratePO = newPayload?.[saleOrderUuid]?.[
        saleOrderItemUuid
      ]?.find((supplyItem) => supplyItem?.generatePo)

      if (valueToGeneratePO) {
        const quantity = getGeneratePoQuantity(
          saleOrderUuid,
          saleOrderItem,
          newPayload,
        )

        const generatePoDate =
          quantity > 0
            ? [
              {
                generatePo: true,
                quantity,
              },
            ]
            : []

        newPayload = {
          ...newPayload,
          [saleOrderUuid]: {
            ...newPayload[saleOrderUuid],
            [saleOrderItemUuid]: [
              ...newPayload[saleOrderUuid][saleOrderItemUuid].filter(
                (supplyItem) => !supplyItem?.generatePo,
              ),
              ...generatePoDate,
            ],
          },
        }
      }
      return newPayload
    })
  }

  const handleChangeCategory = (
    saleOrderUuid: string,
    saleOrderItem: SupplyItemsPreviewResponseSaleOrderItem,
    supplyItemUuid: string,
    category: keyof typeof ProductQualityCategoryEnum,
  ) => {
    setSaleOrdersToPayload((prevPayload) => {
      const newPayload = { ...prevPayload }
      const items = newPayload?.[saleOrderUuid]?.[saleOrderItem?.uuid || '']

      if (items && supplyItemUuid) {
        newPayload[saleOrderUuid][saleOrderItem?.uuid || ''] = items.map(
          (item) =>
            item.uuid === supplyItemUuid ? { ...item, category } : item,
        )
      }

      if (items && !supplyItemUuid) {
        newPayload[saleOrderUuid][saleOrderItem?.uuid || ''] = items.map(
          (item) => (item.generatePo === true ? { ...item, category } : item),
        )
      }

      return newPayload
    })
  }

  const getTags = (
    valueToGeneratePO: GeneratePurchaseOrderPayloadSupplyItemProps | undefined,
    saleOrderUuid: string,
    item: SupplyItemsPreviewResponseSaleOrderItem,
    saleOrdersToPayload: Record<
      string,
      Record<string, GeneratePurchaseOrderPayloadSupplyItemProps[]>
    >,
  ) => {
    const tags = {
      'partial-inventory': (
        <Tag variant="success" model="light">
          {t('componentSelectSupplierOrderItemsTagInventoryPartial')}
        </Tag>
      ),
      inventory: (
        <Tag variant="success" model="light">
          {t('componentSelectSupplierOrderItemsTagInventory')}
        </Tag>
      ),
      po: (
        <Tag variant="info" model="light">
          {t('componentSelectSupplierOrderItemsTagPO')}
        </Tag>
      ),
    }

    const tagToShow = []

    const inventoryItem = saleOrdersToPayload[saleOrderUuid]?.[
      item?.uuid || ''
    ]?.find(
      (supplyItem) =>
        !supplyItem?.generatePo &&
        supplyItem?.quantity &&
        supplyItem?.quantity > 0,
    )

    if (!valueToGeneratePO && inventoryItem) {
      tagToShow.push(tags.inventory)
    } else if (
      (valueToGeneratePO?.quantity || 0) < (item?.missingQuantity || 0) &&
      inventoryItem
    ) {
      tagToShow.push(tags.po)
      tagToShow.push(tags['partial-inventory'])
    } else if (valueToGeneratePO && !inventoryItem) {
      tagToShow.push(tags.po)
    }

    return (
      <div className={styles['tags-container']}>
        {tagToShow.map((tag, index) => (
          <React.Fragment key={index}>{tag}</React.Fragment>
        ))}
      </div>
    )
  }

  const Loading = () => {
    return (
      <div className={styles['info-row-content']}>
        <div className={styles['sale-orders-container']}>
          {[0].map((i) => (
            <div key={i} className={styles['products-card']}>
              <div className={styles['products-card-header']}>
                <div className={styles['company-name-container']}>
                  <Text
                    variant="title-medium"
                    className={styles['company-name-title']}
                  >
                    <Skeleton height="1rem" width="7rem" />
                  </Text>
                  <Skeleton height="1rem" width="10rem" />
                </div>
                <Text variant="title-medium">
                  <Skeleton height="1rem" width="6rem" />
                </Text>
              </div>
              <div className={styles['product-card-body']}>
                <div className={styles['item-card']}>
                  <div className={styles['item-card-header']}>
                    <Skeleton height="1rem" width="4rem" />
                    <Text>
                      <Skeleton height="1rem" width="20rem" />
                    </Text>
                    <div className={styles['item-card-header-total-completed']}>
                      <Text>
                        <Skeleton height="1rem" width="6rem" />
                      </Text>
                    </div>
                  </div>

                  <div className={styles['item-card-body']}>
                    <div className={styles['item-card-body-column']}>
                      <Skeleton height="1rem" width="1rem" />
                      <Text>
                        <Skeleton height="1rem" width="6rem" />
                      </Text>
                    </div>
                    <div className={styles['item-card-body-column']}>
                      <Text>
                        <Skeleton height="1rem" width="6rem" />
                      </Text>
                    </div>
                    <div className={styles['item-card-body-column']}>
                      <Text>
                        <Skeleton height="1rem" width="6rem" />
                      </Text>
                    </div>
                    <div className={styles['item-card-body-column-total']}>
                      <Text>
                        <strong>
                          <Skeleton height="1rem" width="6rem" />
                        </strong>
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={disabled ? styles.disabled : ''}>
      <SectionHeader
        title={`${position}. ${t('componentSelectSaleOrdersTitle')}`}
      >
        <div className={styles['info-block']}>
          <div className={styles['info-row-content']}>
            <Text className={styles['info-item']}>
              {t('componentSelectSaleOrdersCurrencyLabel')}
            </Text>

            <Radio
              value={values?.currency === 'MXN'}
              onChange={() =>
                values?.currency !== 'MXN' ? onChangeCurrency('MXN') : null
              }
            >
              {t('componentSelectSaleOrdersCurrencyMXNLabel')}
            </Radio>
            <Radio
              value={values?.currency === 'USD'}
              onChange={() =>
                values?.currency !== 'USD' ? onChangeCurrency('USD') : null
              }
            >
              {t('componentSelectSaleOrdersCurrencyUSDLabel')}
            </Radio>
          </div>
          <div className={styles['sale-orders-header']}>
            <Button
              variant="ghost"
              className={styles['add-button']}
              onClick={onShowAddSaleOrders}
            >
              {t('componentSelectSaleOrdersAddButton')}
              <AddCircleOutlineIcon />
            </Button>
          </div>
        </div>

        {isLoading &&
          values?.saleOrders?.length &&
          values?.saleOrders?.length > 0 && <Loading />}

        {!isLoading &&
          (!values?.saleOrders?.length || values?.saleOrders?.length === 0) && (
            <>
              <Text className={styles['info-item']}>
                {t('componentSelectSaleOrdersEmptyTitle')}
              </Text>
            </>
          )}

        {!isLoading &&
          values?.saleOrders?.length &&
          values?.saleOrders?.length > 0 ? (
          <div className={styles['info-row-content']}>
            <div className={styles['sale-orders-container']}>
              {preview?.saleOrders?.map((saleOrder, i) => (
                <div
                  key={`sale-order-${i}-${saleOrder?.uuid}`}
                  className={styles['products-card']}
                  style={{
                    display:
                      saleOrder?.items?.filter(
                        (item) => !excludeItems[item?.uuid || ''],
                      )?.length > 0
                        ? 'block'
                        : 'none',
                  }}
                >
                  <div className={styles['products-card-header']}>
                    <div className={styles['company-name-container']}>
                      <Text
                        variant="title-medium"
                        className={styles['company-name-title']}
                      >
                        {saleOrder?.buyer?.companyName || '-'}
                      </Text>
                      {saleOrder?.destinationAddress || '-'}
                    </div>
                    <div className={styles['actions-container']}>
                      <Text variant="title-base">
                        <Link
                          to={`${ROUTES_ADMIN.ORDER_CENTER.ALL.SHOW.fullPath(saleOrder?.uuid || '')}`}
                          target="_blank"
                        >
                          {saleOrder?.reference || '-'}
                        </Link>
                      </Text>
                      <Button
                        variant="secondary"
                        model="round"
                        size="small"
                        onClick={() => removeSaleOrder(saleOrder?.uuid || '')}
                      >
                        <TrashIcon />
                      </Button>
                    </div>
                  </div>
                  {saleOrder?.items.map((item) => {
                    if (item?.uuid && excludeItems[item?.uuid]) return null

                    const valueToGeneratePO = saleOrdersToPayload?.[
                      saleOrder?.uuid || ''
                    ]?.[item?.uuid || '']?.find((val) => val?.generatePo)

                    const dollarExchangeRate = Number(
                      saleOrder?.payments?.[0]?.dollarExchangeRate
                        ? saleOrder?.payments?.[0]?.dollarExchangeRate
                        : 1,
                    )

                    const valueToGeneratePOCost: number =
                      values?.currency === 'USD'
                        ? +(item?.cost || 0)
                        : +(item?.cost || 0) * dollarExchangeRate

                    const supplyItemCompletionStatus =
                      getSupplyItemCompletionStatus(
                        saleOrder?.uuid || '',
                        item,
                        saleOrdersToPayload,
                      )
                    return (
                      <div
                        key={item?.uuid}
                        className={styles['products-card-body']}
                      >
                        <div className={styles['item-card']}>
                          <div className={styles['item-card-header']}>
                            <Switch
                              checked={!excludeItems[item?.uuid || '']}
                              onChange={() =>
                                toggleExcludeItem(item, saleOrder?.uuid || '')
                              }
                            />
                            <Text>{`${item?.title} - ${item?.missingQuantity} KG`}</Text>

                            {getTags(
                              valueToGeneratePO,
                              saleOrder?.uuid || '',
                              item,
                              saleOrdersToPayload,
                            )}

                            <div
                              className={
                                styles['item-card-header-total-completed']
                              }
                            >
                              <Text>
                                {`${supplyItemCompletionStatus.filledQuantity} kg / ${item?.missingQuantity} kg `}{' '}
                                <strong>
                                  ({supplyItemCompletionStatus.percentage}
                                  %)
                                </strong>
                              </Text>
                            </div>
                          </div>
                          <div className={styles['item-card-body']}>
                            <div className={styles['item-card-body-column']}>
                              <Checkbox
                                onChange={() =>
                                  toggleSaleOrdersToPayload(
                                    saleOrder?.uuid || '',
                                    item,
                                    undefined,
                                    { quantity: item?.missingQuantity },
                                    item?.missingQuantity,
                                  )
                                }
                                value={!!valueToGeneratePO}
                              />
                              <Text>{values?.supplier?.name}</Text>
                            </div>
                            <div className={styles['item-card-body-column']}>
                              <EditableColumn
                                type="number"
                                inputProps={{
                                  decimalPrecision: DECIMAL_PLACES_DEFAULT,
                                  prefix:
                                    values?.currency === 'USD'
                                      ? 'USD '
                                      : 'MXN ',
                                }}
                                hasTooltip={false}
                                value={valueToGeneratePOCost}
                                onSubmit={async (value) =>
                                  await handleSaleOrderItemUpdate(
                                    value,
                                    item?.uuid || '',
                                    dollarExchangeRate,
                                  )
                                }
                                render={({ setEditing }) => (
                                  <div
                                    className={
                                      styles['item-card-body-column-with-edit']
                                    }
                                  >
                                    <Text>
                                      {formatCurrency(
                                        valueToGeneratePOCost,
                                        'es-MX',
                                        values?.currency || 'USD',
                                        DECIMAL_PLACES_DEFAULT,
                                      )}
                                    </Text>
                                    <Button
                                      variant="secondary"
                                      model="default"
                                      size="small"
                                      onClick={() => setEditing(true)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  </div>
                                )}
                              />
                            </div>
                            <div className={styles['item-card-body-column']}>
                              <Text>
                                {valueToGeneratePO?.quantity ||
                                  valueToGeneratePO?.quantity === 0
                                  ? valueToGeneratePO?.quantity
                                  : 0}{' '}
                                kg
                              </Text>
                            </div>
                            <div className={styles['item-card-body-column']}>
                              <EditableColumn
                                type="select"
                                inputProps={{
                                  options: [
                                    {
                                      label: t('selectEmpty'),
                                      value: '',
                                    },
                                    ...Object.keys(
                                      ProductQualityCategoryEnum,
                                    ).map((key) => ({
                                      label: tEnums(
                                        `product_quality_category_${key}`,
                                      ),
                                      value: key,
                                    })),
                                  ],
                                }}
                                hasTooltip={false}
                                value={valueToGeneratePO?.category}
                                onSubmit={async (value) =>
                                  handleChangeCategory(
                                    saleOrder?.uuid || '',
                                    item,
                                    '',
                                    value,
                                  )
                                }
                                render={({ setEditing }) => (
                                  <div
                                    className={
                                      styles['item-card-body-column-with-edit']
                                    }
                                  >
                                    <Text>
                                      {valueToGeneratePO?.category
                                        ? tEnums(
                                          `product_quality_category_${valueToGeneratePO?.category}`,
                                        )
                                        : '-'}
                                    </Text>
                                    <Button
                                      variant="secondary"
                                      model="default"
                                      size="small"
                                      onClick={() => setEditing(true)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  </div>
                                )}
                              />
                            </div>
                            <div
                              className={styles['item-card-body-column-total']}
                            >
                              <Text>
                                <strong>
                                  {formatCurrency(
                                    valueToGeneratePOCost *
                                    (valueToGeneratePO?.quantity || 0),
                                    'es-MX',
                                    values?.currency || 'USD',
                                    DECIMAL_PLACES_DEFAULT,
                                  )}
                                </strong>
                              </Text>
                            </div>
                          </div>
                          {item?.supplyOrderItems?.map((supplyItem) => {
                            const supplyItemsByPayload =
                              getSupplyItemsByPayload(
                                saleOrder?.uuid || '',
                                item?.uuid || '',
                                supplyItem?.uuid || '',
                                saleOrdersToPayload,
                              )

                            return (
                              <div
                                key={supplyItem?.uuid}
                                className={styles['item-card-body']}
                              >
                                <div
                                  className={styles['item-card-body-column']}
                                >
                                  <Checkbox
                                    onChange={() =>
                                      toggleSaleOrdersToPayload(
                                        saleOrder?.uuid || '',
                                        item,
                                        supplyItem?.uuid || '',
                                        supplyItem,
                                        item?.missingQuantity,
                                      )
                                    }
                                    value={!!supplyItemsByPayload}
                                  />
                                  <div>
                                    <Text>
                                      {supplyItem?.supplyOrder?.supplier?.name}
                                    </Text>
                                    <div>
                                      <Text
                                        variant="text-small"
                                        className={
                                          styles[
                                          'item-card-body-column-reference'
                                          ]
                                        }
                                      >
                                        {`${supplyItem?.supplyOrder?.reference} | ${supplyItem?.supplyOrder?.supplyQuotation?.purchaseOrderCode}`}
                                      </Text>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className={styles['item-card-body-column']}
                                >
                                  <Text>
                                    {formatCurrency(
                                      getSupplyOrderItemValueByCurrency(
                                        supplyItem,
                                        values?.currency || 'MXN',
                                        supplyItem?.supplyOrder?.payments?.[0]
                                          ?.dollarExchangeRate || '1',
                                        supplyItem?.supplyOrder?.payments?.[0]
                                          ?.currency || 'MXN',
                                      ),
                                      'es-MX',
                                      values?.currency || 'MXN',
                                      DECIMAL_PLACES_DEFAULT,
                                    )}
                                  </Text>
                                </div>
                                <div
                                  className={styles['item-card-body-column']}
                                >
                                  <EditableColumn
                                    value={supplyItemsByPayload?.quantity || 0}
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                      decimalPrecision: 0,
                                      suffix: ' kg',
                                    }}
                                    hasTooltip={false}
                                    onSubmit={async (value) =>
                                      handleChangeQuantity(
                                        saleOrder?.uuid || '',
                                        item,
                                        supplyItem?.uuid || '',
                                        Number(
                                          Number(value) >
                                            (supplyItem?.quantity || 0)
                                            ? supplyItem?.quantity || 0
                                            : value,
                                        ),
                                      )
                                    }
                                    render={({ setEditing }) => (
                                      <>
                                        <div
                                          className={
                                            styles[
                                            'item-card-body-column-quantity'
                                            ]
                                          }
                                        >
                                          <Text>
                                            {supplyItemsByPayload?.quantity ||
                                              '0'}{' '}
                                            kg
                                          </Text>
                                          <Text
                                            variant="text-small"
                                            className={
                                              styles['available-quantity']
                                            }
                                          >
                                            {getAvailableQuantity(
                                              supplyItem,
                                              supplyItemsByPayload || {},
                                            )}{' '}
                                            kg{' '}
                                            {t(
                                              'componentSelectSupplierOrderItemsAvilableQuantity',
                                            )}
                                          </Text>
                                        </div>
                                        <div
                                          className={
                                            styles[
                                            'item-card-body-column-quantity-actions'
                                            ]
                                          }
                                        >
                                          {supplyItem?.supplyOrder?.supplier
                                            ?.email ===
                                            MERCAI_WAREHOUSES_OWNER_EMAIL ? (
                                            <Tag
                                              variant="success"
                                              model="light"
                                            >
                                              {t(
                                                'componentSelectSupplierOrderItemsSupplierStore',
                                              )}
                                            </Tag>
                                          ) : (
                                            <Tag variant="info" model="light">
                                              {t(
                                                'componentSelectSupplierOrderItemsSupplierSupplier',
                                              )}
                                            </Tag>
                                          )}
                                          <Button
                                            variant="secondary"
                                            model="default"
                                            size="small"
                                            onClick={() => setEditing(true)}
                                          >
                                            <EditIcon />
                                          </Button>
                                        </div>
                                      </>
                                    )}
                                  />
                                </div>

                                <div
                                  className={styles['item-card-body-column']}
                                >
                                  <Text>
                                    {supplyItem?.category
                                      ? tEnums(
                                        `product_quality_category_${supplyItem?.category}`,
                                      )
                                      : '-'}
                                  </Text>
                                </div>

                                <div
                                  className={
                                    styles['item-card-body-column-total']
                                  }
                                >
                                  <Text>
                                    <strong>
                                      {formatCurrency(
                                        totalSupplyOrderItemValue(
                                          supplyItem,
                                          supplyItemsByPayload?.quantity || 0,
                                          values?.currency || 'MXN',
                                          supplyItem?.supplyOrder?.payments?.[0]
                                            ?.dollarExchangeRate || '1',
                                          supplyItem?.supplyOrder?.payments?.[0]
                                            ?.currency || 'MXN',
                                        ),
                                        'es-MX',
                                        values?.currency || 'MXN',
                                        DECIMAL_PLACES_DEFAULT,
                                      )}
                                    </strong>
                                  </Text>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {Object.keys(formattedExcludedItems)?.length > 0 &&
          Object.keys(formattedExcludedItems).map((key) => (
            <div key={key} className={styles['excluded-items']}>
              <Text variant="title-medium">
                {t('componentSelectSupplierOrderItemsExcludedItems')} - {key}
              </Text>
              {formattedExcludedItems[key]?.map((item) => (
                <div
                  key={String(item?.uuid)}
                  className={styles['excluded-item']}
                >
                  <Switch
                    checked={false}
                    onChange={() =>
                      toggleExcludeItem(item, String(item?.saleOrderUuid || ''))
                    }
                  />
                  <Text>{String(item?.title)}</Text>
                </div>
              ))}
            </div>
          ))}
      </SectionHeader>
    </div>
  )
}
